import { OptOut } from "commonAssets/components/Forms/OptOut";
import {
    createElement, loadingGif, validateField, clearMessage,
    toggleFieldError, loadMember,
} from 'commonAssets/js/helpers';
import { loading, outputDiv } from 'commonAssets/js/constants';

export class CustomOptOut extends OptOut {
    constructor({ appClaimForm, userLanguage, i18n, onFormAdded, onFormRemoved, onFormSubmit, errorHandler }) {
        super(appClaimForm, userLanguage, i18n, true, false, errorHandler);
        this.formObserver = new MutationObserver(
            mutations => this.handleFormChanges({ mutations, onFormAdded, onFormRemoved })
        );
        this.onFormSubmit = onFormSubmit;
    }

    async form() {
        try {
            const response = await fetch(this.data);
            const json = await response.json();
            const config = json.exclusion_form[this.lang];
            this.init_exclusion(config);
        }
        catch (error) {
            this.errorHandler.log_exception(new Error('Error fetching exclusion form config', { cause: error }));
            this.errorHandler.display_modal(error);
        }
    }

    init_exclusion(config) {
        this.exclusion.api.exclusionDeadlineGet({ domain: this.exclusion.data.domain }).then((result) => {

            // Claim Deadline - Default Pacific DateTime
            let datetime;
            if (Boolean(result.data.pacific_datetime)) {
                datetime = result.data.pacific_datetime;
            }
            else {
                datetime = `<pre>Error: datetime</pre>`;
            }

            let exclusion_deadline;
            if (Boolean(result.data.exclusion_deadline)) {
                exclusion_deadline = result.data.exclusion_deadline;
            }

            // Error Handling
            if (result.data.error) {
                this.setup_result_data_error(result);
                return;
            }

            const container = createElement("div", {
                class: "display-form",
            });

            this.formObserver.observe(container, { childList: true, subtree: true });

            if (config.form_options.notice_confirmation.display) {
                // Init opt out guard
                this.init_exclusion_guard(config, container, datetime, exclusion_deadline);
            }
            else {
                // New Exclusion Form
                container.appendChild(this.exclusion_form(config));
                outputDiv.appendChild(container);

                // Initiate opt out fields
                this.init_exclusion_fields();

                // Submit form
                document.getElementById("form").addEventListener('submit', (process) => {
                    this.process_exclusion(process, this.exclusion, exclusion_deadline);
                });
            }

            loadingGif(loading, "hide", 250);

        }).catch((error) => {
            this.errorHandler.log_exception(new Error('Error fetching exclusion deadline', { cause: error }));
            this.errorHandler.display(error);
        });
    }

    exclusion_form(params) {
        // create the exclusion form
        const claimFormElems = [
            createElement("h3", {
                class: "text-center text-decoration-underline mb-3",
            }, [], params.form_options.title),
            createElement("div", {
                class: "alert alert-info text-dark text-center fw-semibold mb-3",
                id: "exclusion_form_top_notice",
            }, [], params.form_options.top_alert.body),
            createElement("div", {
                class: "text-center mb-3",
            }, [], params.form_options.body),
            this.claim_contact_information(params),
        ];
        // if the claim fields were enabled push to form
        if (this.enableClaimFields) {
            claimFormElems.push(this.claim_fieldset(params));
        }
        // if the transational table is enabled, push to form
        if (this.enableClaimTransactions) {
            claimFormElems.push(this.claim_transactions(params));
        }
        // push the attestation to the form
        claimFormElems.push(this.claim_certification(params));
        // build the form and add elements
        const form = createElement("form", {
            class: "needs-validation",
            id: "form",
            method: "post"
        }, claimFormElems);
        // return the html form
        return form;
    }

    init_exclusion_guard(form, container, datetime, exclusion_deadline) {

        // Guard
        container.appendChild(this.guard(form, datetime));
        outputDiv.appendChild(container);

        const zero_zero_id = document.getElementById('zero_zero_id');
        const confirmation_code = document.getElementById('confirmation_code');
        const skip_guard = document.getElementById("skip-guard");

        // Notice Id and Confirmation Code
        [zero_zero_id, confirmation_code].forEach(function (input) {
            input.addEventListener('change', validateField);
        });

        // Skip Guard
        if (skip_guard) {
            skip_guard.addEventListener("click", () => {
                // Hide guard
                document.getElementById('guard').classList.add("d-none");
                // New Claim Form
                container.appendChild(this.exclusion_form(form));
                container.scrollIntoView({ behavior: "smooth" });
                outputDiv.appendChild(container);

                // Initiate opt out fields
                this.init_exclusion_fields();

                // Submit form
                document.getElementById("form").addEventListener('submit', (process) => {
                    this.process_exclusion(process, this.exclusion, exclusion_deadline);
                });
            });
        }

        // Validate Guard Fields
        document.getElementById("guard").addEventListener("submit", (e) => {
            e.preventDefault();

            var valid = true;
            var member = {
                domain: this.claim.data.domain,
                zero_zero_id: zero_zero_id.value.toUpperCase(),
                confirmation_code: confirmation_code.value,
            };

            clearMessage();

            // Validate guard fields
            [zero_zero_id, confirmation_code].forEach(function (input) {
                valid = validateField(input) && valid;
            });
            if (!valid) return;

            loadingGif(loading, "show");

            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
            this.claim.api.classMemberPost({}, member).then((result) => {

                // Check for error message
                if (result.data.error) {
                    toggleFieldError(zero_zero_id, true, '');
                    toggleFieldError(confirmation_code, true, '');
                    this.errorHandler.log_exception(new Error('Error fetching class member', { cause: result }));
                    this.errorHandler.display_modal(result);
                    return;
                }

                // Add notice id and confirmation code to claim data
                this.exclusion.data.zero_zero_id = member.zero_zero_id;
                this.exclusion.data.confirmation_code = member.confirmation_code;
                this.exclusion.data.google_customer_id = result.data.google_customer_id;

                // Check for existing claim number and add to claim data
                if (result.data.claim_id) {
                    this.exclusion.data.claim_id = result.data.claim_id;
                }

                // Hide guard
                document.getElementById('guard').classList.add("d-none");

                // New Claim Form
                container.appendChild(this.exclusion_form(form));
                container.scrollIntoView({ behavior: "smooth" });
                outputDiv.appendChild(container);

                loadingGif(loading, "hide", 250);

                // Load member data into form
                loadMember(result.data);

                // Init claim fields
                this.init_exclusion_fields(this.exclusion, form);

                // Submit Exclusion
                document.getElementById("form").addEventListener('submit', (process) => {
                    this.process_exclusion(process, this.exclusion, exclusion_deadline);
                });

            }).catch((error) => {
                this.errorHandler.log_exception(new Error('Error fetching class member', { cause: error }));
                this.errorHandler.display_modal(error);
            });
        });
    }

    process_exclusion(e, exclusion, deadline) {
        e.preventDefault();
        loadingGif(loading, "show");
        this.onFormSubmit({ e, ctx: this }).then((isValid) => {
            if (!isValid) {
                return;
            }
            super.process_exclusion(e, exclusion, deadline);
        }).finally(() => {
            loadingGif(loading, "hide");
        });
    }

    /**
    * Handles the mutations.
    * 
    * @param {Object} params - The parameters.
    * @param {MutationRecord[]} params.mutations - The mutations.
    * @param {Function} params.onFormAdded - The function to call when the form is added.
    * @param {Function} params.onFormRemoved - The function to call when the form is removed.
    */
    handleFormChanges({ mutations, onFormAdded, onFormRemoved }) {
        mutations.forEach((mutation) => {
            if (mutation.type !== 'childList') return;

            const addedForm = findForm(mutation.addedNodes);
            const removedForm = findForm(mutation.removedNodes);

            if (addedForm) {
                onFormAdded({ ctx: this, form: addedForm });
            }
            else if (removedForm) {
                this.formObserver.disconnect();
                onFormRemoved({ ctx: this, form: removedForm });
            }
        });
    }
}

/**
* Finds a form in the child list.
* 
* @param {NodeList} childList - The child list.
* @returns {Node} - The form.
*/
function findForm(childList) {
    return Array.from(childList).find((node) => node.id === "form");
}